import api from "@/lib/axiosConfig";
import { AppFile } from "@/api/file";
import { Multiplesample } from "@/lib/types";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

interface MultipleSampleDTO {
  where: any;
  order: any;
  take: number;
  skip: number;
}

const getAllMultipleSamples = async ({
  where,
  order,
  take,
  skip,
}: MultipleSampleDTO): Promise<Multiplesample[]> => {
  const { data } = await api.post<Multiplesample[]>(
    `back/api/multiplesample?take=${take || 10}&skip=${skip || 0}`,
    {
      where,
      orderBy: order,
    },
  );
  return data;
};

export const useGetAllMultipleSamples = ({
  where,
  order,
  take,
  skip,
}: MultipleSampleDTO) => {
  return useQuery({
    queryKey: ["multiplesamples", where, order, take, skip],
    queryFn: () => getAllMultipleSamples({ where, order, take, skip }),
    placeholderData: keepPreviousData,
  });
};

export async function getCountByStatus(
  multipleprintId: string,
  where?: object,
): Promise<{ published: number; draft: number }> {
  const { data } = await api.post<{ published: number; draft: number }>(
    `back/api/multiplesample/countByStatus/${multipleprintId}`,
    { where },
  );
  return data;
}

interface ExemplairesByMultipleprintDTO {
  multipleprintId: string;
  where: object;
  order: object[];
  take: number;
  skip: number;
}

export const useGetExemplairesByMultipleprint = ({
  multipleprintId,
  where,
  order,
  take,
  skip,
}: ExemplairesByMultipleprintDTO) => {
  const mergedWhere = {
    ...where,
    multipleprints: { some: { id: multipleprintId } },
  };
  return useGetAllMultipleSamples({
    where: mergedWhere,
    order,
    take,
    skip,
  });
};

export type CreateMultipleSampleParams = {
  exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement?: string;
  exemplaireDansEstampesMultiplesDescriptionNumero?: string;
  exemplaireDansEstampesMultiplesDescriptionNumeroInventaire?: string;
  exemplaireDansEstampesMultiplesDescriptionLocalisation?: string;
  exemplaireDansEstampesMultiplesDescriptionNotes?: string;
  multipleprints?: { connect: { id: string } };
  copyFromMaster?: boolean;
};

async function createMultipleSample(multiplesample: CreateMultipleSampleParams): Promise<Multiplesample> {
  const { data } = await api.post<Multiplesample>(`back/api/multiplesample/create`, multiplesample);
  return data;
}

export const useCreateMultipleSample = () => {
  const queryClient = useQueryClient();
  return useMutation<Multiplesample, Error, CreateMultipleSampleParams>({
    mutationFn: createMultipleSample,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["multiplesamples"] });
      queryClient.invalidateQueries({ queryKey: ["multiplesample", data.id] });
      queryClient.invalidateQueries({ queryKey: ["countByStatus", "multiplesample"], exact: false });
    },
  });
};

export type UpdateMultipleSampleParams = {
  data: Partial<CreateMultipleSampleParams> & Record<string, unknown>;
  where: { id: string };
};

async function updateMultipleSample(params: UpdateMultipleSampleParams) {
  const { data } = await api.post(`back/api/multiplesample/update`, {
    data: params.data,
    where: params.where,
  });
  return data;
}

export const useUpdateMultipleSample = () => {
  const queryClient = useQueryClient();
  return useMutation<Multiplesample, Error, UpdateMultipleSampleParams>({
    mutationFn: updateMultipleSample,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["multiplesamples"] });
      queryClient.invalidateQueries({ queryKey: ["multiplesample", data.id], exact: false });
      queryClient.invalidateQueries({ queryKey: ["countByStatus", "multiplesample"], exact: false });
    },
  });
};

async function deleteMultipleSample(id: string) {
  const { data } = await api.post(`back/api/multiplesample/delete`, {
    where: { id },
  });
  return data;
}

export const useDeleteMultipleSample = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMultipleSample,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["multiplesamples"] });
      queryClient.invalidateQueries({ queryKey: ["countByStatus", "multiplesample"], exact: false });
    },
  });
};

export type DuplicateMultipleSampleParams = {
  sourceId: string;
  exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement: string;
};

async function duplicateMultipleSample(
  params: DuplicateMultipleSampleParams,
): Promise<Multiplesample> {
  const { data } = await api.post<Multiplesample>(
    "back/api/multiplesample/duplicate",
    params,
  );
  return data;
}

export const useDuplicateMultipleSample = () => {
  const queryClient = useQueryClient();
  return useMutation<Multiplesample, Error, DuplicateMultipleSampleParams>({
    mutationFn: duplicateMultipleSample,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["multiplesamples"] });
      queryClient.invalidateQueries({ queryKey: ["multiplesample", data.id] });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "multiplesample"],
        exact: false,
      });
    },
  });
};

async function getMultipleSampleById(id: string) {
  const { data } = await api.get(`back/api/multiplesample/${id}`);
  return data;
}

export const useGetMultipleSampleById = (id: string | undefined) => {
  return useQuery({
    queryKey: ["multiplesample", id],
    queryFn: () => getMultipleSampleById(id!),
    enabled: !!id,
  });
};

/** Documents administratifs (certificats) stockés sur l'exemplaire. */
export const getMultiplesampleAdministrativeDocuments = async (
  multiplesampleId: string,
) => {
  const { data } = await api.get<AppFile[]>(
    `back/api/multiplesample/${multiplesampleId}/administrative-documents`,
  );
  return data;
};

export const useGetMultiplesampleAdministrativeDocuments = (
  multiplesampleId: string,
) => {
  return useQuery({
    queryKey: [
      "multiplesample",
      multiplesampleId,
      "administrative-documents",
    ],
    queryFn: () => getMultiplesampleAdministrativeDocuments(multiplesampleId),
    enabled: !!multiplesampleId,
  });
};
